import { Drawer, Button, Space, Grid, Tabs } from "antd";
import { PlusOutlined, CloseOutlined } from "@ant-design/icons";
import { useVictimsAndKillers } from "../../../hooks/useVictimsAndKillers";
import FilterRow from "./FilterRow";
import DrawerTitleActions from "./DrawerTitleActions";
import MarkersTab from "./MarkersTab";
import TimelineTab from "./TimelineTab";
import type { SerialKillerListItemDTO } from "@shared/contracts";
import { useSerialKillers } from "../../../hooks/queries/useSerialKillers";
import { DRAWER_WIDTH_PX } from "../../../utils/constants";
import styled from "styled-components";

// Styled components
const FilterDrawer = styled(Drawer)`
  .ant-drawer-content {
    padding-left: 12px;
    padding-right: 12px;
    padding-top: 8px;
    padding-bottom: 8px;
    background: #222831 !important;
  }
  .ant-drawer-header {
    background: #222831;
    color: #eeeeee;
    padding-bottom: 0;
  }
  .ant-drawer-body {
    padding-top: 0px;
    background: #222831;
  }
  .ant-drawer-title {
    color: #8e1616;
    font-weight: bold;
    font-size: 24px;
    letter-spacing: 1px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  @media (max-width: 768px) {
    .ant-drawer-content-wrapper {
      width: 100% !important;
    }
  }
`;

const LogoImg = styled.img`
  height: 48px;
  margin-right: -6px;
  vertical-align: middle;
`;

const AddRowButton = styled(Button)`
  border-radius: 8px;
  color: #eeeeee !important;
  background: #393e46 !important;
  margin-bottom: 12px;
  margin-top: 12px;
  &:hover {
    color: #8e1616 !important;
    background: #222831 !important;
    border-color: #8e1616 !important;
  }
`;

const FilterSpace = styled(Space)`
  width: 100%;
`;

const DrawerTabs = styled(Tabs)`
  .ant-tabs-nav {
    position: sticky;
    top: 0;
    z-index: 10;
    background: #222831;
    margin-bottom: 12px;
    box-shadow: 0 4px 8px 0 #222831;
  }
  .ant-tabs-nav-wrap {
    overflow: visible !important;
  }
  .ant-tabs-nav-list {
    transform: none !important;
    width: 100%;
  }
  .ant-tabs-tab {
    flex: 1;
    justify-content: center;
    color: #aaaaaa !important;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #8e1616 !important;
  }
  .ant-tabs-ink-bar {
    background: #8e1616 !important;
  }
  .ant-tabs-nav::before {
    border-bottom-color: #393e46 !important;
  }
`;

const CloseButton = styled(Button)`
  color: #eeeeee !important;
  background: transparent !important;
  border: none !important;
  font-size: 20px;
  &:hover {
    color: #8e1616 !important;
    background: #393e46 !important;
  }
`;

const TitleWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

export interface ControlsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function ControlsDrawer({ open, onClose }: ControlsDrawerProps) {
  const screens = Grid.useBreakpoint();
  const {
    filters: rows,
    addFilter: addRow,
    setFilterColor: setRowColor,
  } = useVictimsAndKillers();
  const { data = [], isLoading } = useSerialKillers();

  return (
    <FilterDrawer
      title={
        <TitleWrapper>
          <LogoImg src="/logo.png" alt="Logo" />
          Crime Stalkers
          <DrawerTitleActions />
        </TitleWrapper>
      }
      placement="left"
      mask={false}
      getContainer={false}
      open={open}
      onClose={onClose}
      width={screens.md ? DRAWER_WIDTH_PX : "100%"}
      closable={false}
      extra={
        <CloseButton
          type="text"
          aria-label="Close"
          onClick={onClose}
          icon={<CloseOutlined />}
        />
      }
    >
      <DrawerTabs
        items={[
          {
            key: "crimes",
            label: "Crimes",
            children: (
              <FilterSpace direction="vertical" size={12}>
                <AddRowButton
                  icon={<PlusOutlined />}
                  onClick={addRow}
                  type="dashed"
                  block
                >
                  Add more crimes
                </AddRowButton>
                {rows
                  .slice()
                  .reverse()
                  .map((row) => (
                    <FilterRow
                      key={row.id}
                      id={row.id}
                      color={row.color}
                      onColorChange={(hex) => setRowColor(row.id, hex)}
                      killers={data as SerialKillerListItemDTO[]}
                      loading={isLoading}
                    />
                  ))}
              </FilterSpace>
            ),
          },
          {
            key: "markers",
            label: "Markers",
            children: <MarkersTab />,
          },
          {
            key: "timeline",
            label: "Timeline",
            children: <TimelineTab />,
          },
        ]}
      />
    </FilterDrawer>
  );
}
